/**
 * OC Ask Human Provider
 * Context provider for Ask Human modal state
 * Follows the pattern from the original AskHumanProvider
 */

import React, { createContext, ReactNode, useContext, useState } from 'react';

import { OcChatConfig } from '../../../../types/oc';
import { SubmitDiscussionResponse } from '../../types/apiTypes';

type OcAskHumanContextValue = {
  config: OcChatConfig;
  isOpen: boolean;
  toggleModal: () => void;
  onSuccess?: (response: SubmitDiscussionResponse) => void;
  onError?: (error: Error) => void;
};

const OcAskHumanContext = createContext<OcAskHumanContextValue | null>(null);
OcAskHumanContext.displayName = 'OcAskHumanContext';

/**
 * Hook to access Ask Human context
 * Throws if used outside provider
 */
export const useOcAskHumanContext = (): OcAskHumanContextValue => {
  const context = useContext(OcAskHumanContext);
  if (!context) {
    throw new Error('useOcAskHumanContext must be used within OcAskHumanProvider');
  }
  return context;
};

export type OcAskHumanProviderProps = {
  config: OcChatConfig;
  onSuccess?: (response: SubmitDiscussionResponse) => void;
  onError?: (error: Error) => void;
  children: ReactNode;
};

/**
 * Provider for Ask Human modal state
 * Matches the pattern from original AskHumanProvider
 */
export const OcAskHumanProvider: React.FC<OcAskHumanProviderProps> = ({
  config,
  onSuccess,
  onError,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <OcAskHumanContext.Provider
      value={{
        config,
        isOpen,
        toggleModal,
        onSuccess,
        onError,
      }}
    >
      {children}
    </OcAskHumanContext.Provider>
  );
};

